import React, { useState } from 'react';
import { Collapse } from 'react-bootstrap';
import { Plus, Edit2 } from 'lucide-react';
import moment from 'moment';
import { toast } from 'sonner';
import UpdateCompensationModal from './UpdateCompensationModal';
import { Section, Facts, Fact, UpcomingBadge, HistoryToggle, HistoryTable, SectionEmpty, formatDate, formatPeriod } from './EmployeeDetailParts';

const CompensationCard = ({ employeeId, companyId, currentCompensation, history, currentContract, onUpdate }) => {
  const [showHistory, setShowHistory] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'

  const handleCreateClick = () => {
    if (!currentContract) {
      toast.error('Ett anställningskontrakt måste finnas innan ersättning kan läggas till');
      return;
    }
    setModalMode('create');
    setShowUpdateModal(true);
  };

  const handleEditClick = () => {
    setModalMode('edit');
    setShowUpdateModal(true);
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return `${parseFloat(amount).toLocaleString('sv-SE')} SEK`;
  };

  const formatDate = (date) => {
    return moment(date).format('DD MMM YYYY');
  };

  const handleUpdateSuccess = () => {
    setShowUpdateModal(false);
    if (onUpdate) onUpdate();
  };

  const noun = currentCompensation?.pay_frequency === 'hourly' ? 'Timlön'
    : currentCompensation?.pay_frequency === 'weekly' ? 'Veckolön'
    : currentCompensation?.pay_frequency === 'daily' ? 'Daglön' : 'Månadslön';

  return (
    <>
      <Section
        id="compensation-heading"
        title="Ersättning"
        badge={<UpcomingBadge show={Boolean(currentCompensation?._isFuture)} />}
        meta={currentCompensation
          ? `${currentCompensation._isFuture ? 'Börjar' : 'Gäller från'} ${formatDate(currentCompensation.valid_from)}`
          : 'Ingen ersättning registrerad'}
        actions={currentCompensation && (
          <>
            <button type="button" className="ll-btn ll-btn--ghost ll-btn--sm" onClick={handleEditClick} aria-label="Redigera ersättning">
              <Edit2 />
              Ändra
            </button>
            <button type="button" className="ll-btn ll-btn--secondary ll-btn--sm" onClick={handleCreateClick} aria-label="Skapa ny ersättning för anställd">
              <Plus />
              Ny ersättning
            </button>
          </>
        )}
      >
        {currentCompensation ? (
          <>
            <Facts>
              <Fact label="Månadslön" tone="strong">{formatCurrency(currentCompensation.amount_monthly)}</Fact>
              <Fact label="Timlön">{formatCurrency(currentCompensation.amount_hourly)}</Fact>
              <Fact label="Daglön">{formatCurrency(currentCompensation.amount_daily)}</Fact>
              <Fact label="Veckolön">{formatCurrency(currentCompensation.amount_weekly)}</Fact>
              <Fact label="Löneform">{noun}</Fact>
              <Fact label="Valuta">{currentCompensation.currency || 'SEK'}</Fact>
              <Fact label="Arbetstid">{currentCompensation.work_week_hours ? `${currentCompensation.work_week_hours} h/vecka` : null}</Fact>
            </Facts>

            <HistoryToggle
              open={showHistory}
              onToggle={() => setShowHistory(!showHistory)}
              count={history && history.length > 1 ? history.length - 1 : 0}
              noun="lönehistorik"
            />
            <Collapse in={showHistory}>
              <div>
                {history && history.length > 1 && (
                  <HistoryTable
                    columns={['Period', 'Månadslön', 'Löneform']}
                    rows={history}
                    rowKey="comp_id"
                    render={(comp) => [formatPeriod(comp), formatCurrency(comp.amount_monthly), comp.pay_frequency]}
                  />
                )}
              </div>
            </Collapse>
          </>
        ) : (
          <SectionEmpty
            action={
              <button type="button" className="ll-btn ll-btn--secondary ll-btn--sm" onClick={handleCreateClick} aria-label="Lägg till ersättning för anställd">
                <Plus />
                Lägg till lön
              </button>
            }
          >
            Lägg till löneuppgifter för att kunna köra lön och skapa lönespecifikationer.
          </SectionEmpty>
        )}
      </Section>

      <UpdateCompensationModal
        show={showUpdateModal}
        handleClose={() => setShowUpdateModal(false)}
        employeeId={employeeId}
        companyId={companyId}
        currentCompensation={modalMode === 'edit' ? currentCompensation : null}
        currentContract={currentContract}
        mode={modalMode}
        onSuccess={handleUpdateSuccess}
      />
    </>
  );
};

export default CompensationCard;
